import { jsx } from "panel";
import { selx } from "clsx";
import { SectionEl } from "common/components/section";
import { SelectTheme } from "common/components/select/constants";
import { SelectEl } from "common/components/select";
import { TooltipEl } from "common/components/tooltip";
import { BucketSizeEl } from "common/widgets/bucket-sizes";
import { SvgIconEl } from "common/components/svg-icon";
import { mpColors } from "common/util/colors";
import { Directions } from "common/components/drop-menu/types";
import { ButtonSize } from "common/components/button/constants";
import { DisplaySize, Placement } from "common/components/tooltip/types";
import { SwitchEl } from "common/components/switch";
import { getCurrentMoment } from "common/util/date";
import { useRetentionConsistency } from "common/report/util/feature-sampling";
import { TimeUnit } from "common/widgets/date-range-picker/models/types";
import { RetentionAlignmentType } from "common/report/retention/util/types";
import { CUSTOM_RETENTION_UNIT } from "./constants";
import styles from "./index.module.styl";
const commonTooltipAttrs = {
  size: DisplaySize.Small
};
const CustomBucket = ({
  customRetentionUnitSelected,
  bucketSizes,
  retentionUnit,
  onApplyCustom,
  onCancelCustom
}) => {
  return /* @__PURE__ */ jsx(null, null, customRetentionUnitSelected && /* @__PURE__ */ jsx(TooltipEl, {
    attrs: { text: `Returning users on days: ${bucketSizes}` }
  }), customRetentionUnitSelected && /* @__PURE__ */ jsx(BucketSizeEl, {
    sel: selx(styles.bucketSizes),
    attrs: {
      header: `Custom Retention`,
      "bucket-unit": retentionUnit,
      "bucket-sizes": bucketSizes
    },
    on: {
      apply: onApplyCustom,
      cancel: onCancelCustom
    }
  }));
};
export function template() {
  var _a, _b;
  const dateFormat = `MMM DD`;
  const timezone = (_b = (_a = this.getContext(`currentProject`).get()) == null ? void 0 : _a.value) == null ? void 0 : _b.timezone;
  const today = getCurrentMoment({ timezone }).format(dateFormat);
  const tomorrow = getCurrentMoment({ timezone }).add(1, `days`).format(dateFormat);
  const bucketLength = this.attr(`bucket-sizes`).length;
  const selectedRetentionUnitValue = bucketLength !== 0 ? CUSTOM_RETENTION_UNIT : this.attr(`retention-unit`);
  return /* @__PURE__ */ jsx(SectionEl, {
    sel: selx(styles.section, { [styles.noPadding]: useRetentionConsistency() }),
    attrs: {
      "header-title": `Retention Criteria`,
      "is-container": true,
      "non-collapsible": true
    }
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: selx(styles.select, styles.advancedMenu),
    attrs: {
      slot: `actions`,
      theme: SelectTheme.Tertiary,
      "always-show-caret": true,
      "selected-option": `Advanced`,
      "menu-alignment": Directions.Right,
      size: ButtonSize.Small,
      tooltip: { placement: Placement.Right },
      "disable-first-item-padding": useRetentionConsistency(),
      "experimental-positioning": true,
      "menu-width": 185
    },
    props: {
      sections: [
        useRetentionConsistency() && {
          label: `Return Criteria`,
          icon: `info-xs`,
          tooltip: {
            text: `Return Criteria`,
            ...commonTooltipAttrs
          },
          items: [
            {
              label: `Within Interval`,
              value: `bounded`,
              isSelected: !this.attr(`unbounded`),
              tooltip: {
                text: `Best for evaluating the percentage of users who return in particular time units.
[Learn More](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-criteria---on-time-interval)`,
                ...commonTooltipAttrs
              }
            },
            {
              label: `Within or after Interval`,
              value: `unbounded`,
              tooltip: {
                text: `Best for overall evaluation of how long users are retained (the opposite of overall churn).
[Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-criteria--on-or-after-time-interval)`,
                ...commonTooltipAttrs
              }
            }
          ]
        },
        useRetentionConsistency() ? {
          label: `Interval Criteria`,
          icon: `info-xs`,
          tooltip: {
            text: `Interval Criteria`,
            ...commonTooltipAttrs
          },
          items: [
            {
              label: `Rolling Interval`,
              value: RetentionAlignmentType.Birth,
              tooltip: {
                text: this.attr(`bucket-sizes`).length > 0 ? `Custom interval` : {
                  [TimeUnit.Day]: `24 hour interval`,
                  [TimeUnit.Week]: `7 day interval`,
                  [TimeUnit.Month]: `30 day interval`
                }[this.attr(`retention-unit`)],
                ...commonTooltipAttrs
              },
              isSelected: this.attr(`retention-alignment-type`) === RetentionAlignmentType.Birth
            },
            {
              label: `Calendar Date`,
              value: RetentionAlignmentType.IntervalStart,
              tooltip: {
                ...commonTooltipAttrs,
                "max-width": `185px`,
                text: `Enable calendar date intervals instead of the default rolling intervals.
E.g. ${today}, ${tomorrow} instead of Day 1, Day 2`
              },
              isSelected: this.attr(`retention-alignment-type`) === RetentionAlignmentType.IntervalStart
            }
          ]
        } : {
          items: [
            {
              label: `Group by date`,
              tooltip: {
                placement: Placement.Top,
                "max-width": `185px`,
                text: `Enable calendar date intervals instead of the default rolling intervals.
E.g. ${today}, ${tomorrow} instead of Day 1, Day 2`
              },
              actionsRenderer: () => /* @__PURE__ */ jsx(SwitchEl, {
                attrs: {
                  on: this.attr(`retention-alignment-type`) === RetentionAlignmentType.IntervalStart
                },
                on: {
                  change: this.handleToggleRetentionAlignment
                }
              })
            }
          ]
        }
      ].filter(Boolean)
    },
    on: {
      change: this.handleAdvancedMenuChange
    }
  }), useRetentionConsistency() && /* @__PURE__ */ jsx("div", null, /* @__PURE__ */ jsx(SelectEl, {
    sel: selx(styles.select, styles.retentionUnitSelect),
    attrs: {
      "menu-width": 250,
      theme: SelectTheme.Tertiary,
      "always-show-caret": this.state.hovered,
      icon: {
        leftIcon: `stop-watch`,
        rightIcon: `double-caret`
      },
      label: this.getLabel(
        this.attr(`bucket-sizes`),
        this.attr(`retention-unit`),
        this.attr(`retention-alignment-type`)
      ),
      "disable-first-item-padding": true,
      sections: [
        {
          label: `Group Entry by`,
          icon: `info-xs`,
          tooltip: {
            "max-width": `140px`,
            placement: Placement.Right,
            text: `Select an interval for which retention groups are generated [Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#custom-retention-brackets)`
          },
          items: [
            {
              value: TimeUnit.Day,
              label: `Day interval`,
              isSelected: selectedRetentionUnitValue === TimeUnit.Day,
              tooltip: {
                text: this.getRetentionUnitTooltip(TimeUnit.Day),
                ...commonTooltipAttrs
              }
            },
            {
              value: TimeUnit.Week,
              label: `Week interval`,
              isSelected: selectedRetentionUnitValue === TimeUnit.Week,
              tooltip: {
                text: this.getRetentionUnitTooltip(TimeUnit.Week),
                ...commonTooltipAttrs
              }
            },
            {
              value: TimeUnit.Month,
              label: `Month interval`,
              isSelected: selectedRetentionUnitValue === TimeUnit.Month,
              tooltip: {
                text: this.getRetentionUnitTooltip(TimeUnit.Month),
                ...commonTooltipAttrs
              }
            },
            {
              value: CUSTOM_RETENTION_UNIT,
              label: `Custom interval`,
              isSelected: selectedRetentionUnitValue === CUSTOM_RETENTION_UNIT,
              tooltip: {
                text: `Select custom intervals`,
                ...commonTooltipAttrs
              }
            }
          ]
        }
      ]
    },
    on: {
      change: this.handleChangeUnitSelector,
      mouseenter: this.handleMouseEnterUnitSelector,
      mouseleave: this.handleMouseLeaveUnitSelector
    }
  }), /* @__PURE__ */ jsx(CustomBucket, {
    customRetentionUnitSelected: this.state.customRetentionUnitSelected,
    bucketSizes: this.attr(`bucket-sizes`),
    retentionUnit: this.attr(`retention-unit`),
    onApplyCustom: this.handleApplyCustom,
    onCancelCustom: this.handleCancelCustom
  })), !useRetentionConsistency() && /* @__PURE__ */ jsx("div", {
    sel: selx(styles.retentionCriteria)
  }, /* @__PURE__ */ jsx("div", {
    sel: selx(styles.iconWrapper)
  }, /* @__PURE__ */ jsx(SvgIconEl, {
    attrs: { icon: `stop-watch`, color: mpColors[`$gray3`] }
  })), /* @__PURE__ */ jsx(SelectEl, {
    sel: selx(styles.select, styles.methodSelect),
    attrs: {
      sections: [
        {
          items: [
            {
              label: `On or After`,
              value: `unbounded`,
              isSelected: this.attr(`unbounded`),
              tooltip: {
                text: `Best for overall evaluation of how long users are retained (the opposite of overall churn).
[Learn more](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-criteria--on-or-after-time-interval)`,
                ...commonTooltipAttrs
              }
            },
            {
              label: `On`,
              isSelected: !this.attr(`unbounded`),
              value: `bounded`,
              tooltip: {
                text: `Best for evaluating the percentage of users who return in particular time units.
[Learn More](https://docs.mixpanel.com/docs/analysis/reports/retention#retention-criteria---on-time-interval)`,
                ...commonTooltipAttrs
              }
            }
          ]
        }
      ],
      "menu-width": 190,
      theme: SelectTheme.Tertiary
    },
    on: { change: this.handleChangeCountingMethodSelector }
  }), /* @__PURE__ */ jsx("div", {
    sel: selx(styles.unitSelect)
  }, /* @__PURE__ */ jsx(SelectEl, {
    sel: selx(styles.select, styles.unitSelect),
    attrs: {
      elref: `retUnitSelector`,
      sections: [
        {
          items: [
            {
              value: TimeUnit.Day,
              isSelected: selectedRetentionUnitValue === TimeUnit.Day,
              label: `Day`,
              tooltip: {
                text: `Calculate users that came back in day intervals`,
                ...commonTooltipAttrs
              }
            },
            {
              value: TimeUnit.Week,
              isSelected: selectedRetentionUnitValue === TimeUnit.Week,
              label: `Week`,
              tooltip: {
                text: `Calculate users that came back in week intervals`,
                ...commonTooltipAttrs
              }
            },
            {
              value: TimeUnit.Month,
              isSelected: selectedRetentionUnitValue === TimeUnit.Month,
              label: `Month`,
              tooltip: {
                text: `Calculate users that came back in month intervals`,
                ...commonTooltipAttrs
              }
            },
            {
              value: CUSTOM_RETENTION_UNIT,
              isSelected: selectedRetentionUnitValue === CUSTOM_RETENTION_UNIT,
              label: `Custom`,
              tooltip: {
                text: `Select custom intervals`,
                ...commonTooltipAttrs
              }
            }
          ]
        }
      ],
      label: this.getLabel(
        this.attr(`bucket-sizes`),
        this.attr(`retention-unit`),
        this.attr(`retention-alignment-type`)
      ),
      theme: SelectTheme.Tertiary,
      tooltip: this.getUnitSelectorTooltip()
    },
    on: { change: this.handleChangeUnitSelector }
  })), /* @__PURE__ */ jsx(CustomBucket, {
    customRetentionUnitSelected: this.state.customRetentionUnitSelected,
    bucketSizes: this.attr(`bucket-sizes`),
    retentionUnit: this.attr(`retention-unit`),
    onApplyCustom: this.handleApplyCustom,
    onCancelCustom: this.handleCancelCustom
  })));
}
